<script setup lang="ts">
const agencyStore = useAgencyStore();
const { agency, hasAgencyAccess, isLoadingAgency } = storeToRefs(agencyStore);

const companyStore = useCompanyStore();
const { company } = storeToRefs(companyStore);

const confirmModal = useConfirmModal();
const { t } = useI18n();

// Track the previous company value for reset on cancel
const previousCompanyId = ref<string | null>(null);

// Function to reset the selector to the previous value
const resetSelector = () => {
  nextTick(() => {
    const selectElement = document.querySelector('select') as HTMLSelectElement;
    if (selectElement && previousCompanyId.value) {
      selectElement.value = previousCompanyId.value;
    }
  });
};

const selectCompany = async (event: Event) => {
  const target = event.target as HTMLSelectElement;
  const newCompanyId = target.value;

  // Store the current company ID before attempting to switch
  previousCompanyId.value = company.value?.uuid || null;

  // Show confirmation modal
  return new Promise<void>((resolve) => {
    confirmModal.onConfirm(async () => {
      try {
        await agencyStore.switchCompany(newCompanyId);
        showSnackbar({
          autoClose: true,
          variant: 'success',
          title: t('components.companySelector.switchSuccess'),
        });

        // Reload the page after successful switch
        window.location.reload();
      }
      catch (error) {
        console.error('Failed to switch company:', error);
        showSnackbar({
          autoClose: true,
          variant: 'error',
          title: t('components.companySelector.switchError'),
        });

        // Reset selector to previous value on error
        resetSelector();
      }
      resolve();
    });

    confirmModal.onCancel(() => {
      // Reset selector to previous value on cancel
      resetSelector();
      resolve();
    });

    confirmModal.reveal(t('components.companySelector.switchConfirm'));
  });
};

// Initialize agency data on component mount (only if not already loaded)
onMounted(() => {
  if (!agency.value && !isLoadingAgency.value) {
    agencyStore.fetchAgency();
  }
});
</script>

<template>
  <SBLoadingSkeleton v-if="!company?.name" class="mx-1 my-2 h-7" />
  <div v-else class="mx-1 my-2 flex items-center justify-center">
    <select
      v-if="hasAgencyAccess"
      class="max-w-[110px] rounded-lg bg-white py-2 pl-2 text-base font-semibold outline-none"
      :disabled="isLoadingAgency"
      @change="selectCompany"
    >
      <option v-if="isLoadingAgency" disabled selected>
        {{ $t('components.companySelector.loading') }}
      </option>
      <option v-else-if="!company" disabled selected>
        {{ $t('components.companySelector.selectCompany') }}
      </option>
      <option
        v-for="agency_company in agency?.companies || []"
        :key="agency_company.uuid"
        :value="agency_company.uuid"
        :selected="company?.uuid === agency_company.uuid"
        class="text-sm"
      >
        {{ agency_company.name }}
      </option>
    </select>
    <h1 v-else class="text-base font-semibold">
      {{ company?.name }}
    </h1>
  </div>
</template>
